import React, { useState } from 'react';
import CustomCircularProgress from 'components/CustomCircularProgress/CustomCircularProgress';
import KeyboardArrowDown from '@material-ui/icons/KeyboardArrowDown';
import Done from '@material-ui/icons/Done';
import Close from '@material-ui/icons/Close';
import { translate } from 'components/Translation';
import styles from 'assets/jss/material-dashboard-pro-react/components/alternativeSelectStyle';
import classNames from 'classnames';
import { CircularProgress, makeStyles } from '@material-ui/core';
import Business from '@material-ui/icons/Business';
import CustomAvatar from 'components/CustomAvatar/CustomAvatar';
import { BodyM, BodyS } from 'components/Typography/Typography';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import CustomInput from 'components/CustomInput/CustomInput';
import SearchIcon from '@material-ui/icons/Search';
// @ts-ignore
const useStyles = makeStyles(styles);

const sizes = {
	sm: 40,
	md: 48,
};

interface ISelectItemProps {
	type: 'sm' | 'md';
	children: React.ReactElement | string;
	disableMargin?: boolean;
}

export const SelectItem = ({ type, children, ...props }: ISelectItemProps) => {
	return type === 'sm' ? <BodyS {...props}>{children}</BodyS> : <BodyM {...props}>{children}</BodyM>;
};

export interface IAlternativeSelectItem {
	id: string | null;
	name: string;
	icon?: React.ReactElement;
	key?: string;
	documentId?: number;
}

const isSelectItem = (item: any): item is IAlternativeSelectItem => {
	return item instanceof Object || Object.keys(item).includes('id');
};

interface IAlternativeSelectProps {
	value: React.ReactElement;
	items: Array<string | IAlternativeSelectItem>;
	customIcons?: boolean;
	selectedItems: Array<string | null>;
	onClick: (item: any) => void;
	label?: string;
	disabled?: boolean;
	error?: string | null;
	className?: string;
	loading?: boolean;
	clear?: () => void;
	type?: 'sm' | 'md';
	withHorizontalPadding?: boolean;
	showError?: boolean;
	id?: string;
	isMenuItemDisabled?: (item: string | IAlternativeSelectItem) => boolean;
	showLoadingWhenItemDisabled?: boolean;
	showBorder?: boolean;
	renderChildren?: boolean;
	children?: React.ReactElement; // this prop is used when we want to customize the dropdown, instead of having default drop down we can pass anything in children
	setRenderChildren?: Function;
	dropdownIcon?: React.ReactElement; //to change the icon of dropdown
	dropdownIconClick?: Function; //to custom click on dropdown
	showItems?: boolean; //to hide items for custom dropdown
	searchOption?: boolean; //to provide search in items list
	searchValue?: string;
	setSearchValue?: Function;
	hideOnBlur?: boolean;
}
const AlternativeSelect = ({
	items,
	value,
	onClick,
	selectedItems,
	label,
	className,
	customIcons,
	disabled,
	error,
	loading,
	clear,
	type = 'sm',
	withHorizontalPadding,
	showError,
	id,
	isMenuItemDisabled = (item: string | IAlternativeSelectItem) => false,
	showLoadingWhenItemDisabled = false,
	showBorder,
	renderChildren,
	children,
	setRenderChildren,
	dropdownIcon,
	dropdownIconClick,
	showItems = true,
	hideOnBlur = true,
	searchOption = false,
	searchValue,
	setSearchValue,
}: IAlternativeSelectProps) => {
	const height = React.useMemo(() => sizes[type] || sizes.sm, [type]);

	const [activeItem, setActiveItem] = React.useState(0);
	const [isOpen, setIsOpen] = useState(false);

	const classes = useStyles({ sizes });

	const selectClasses = classNames({
		[classes.select]: true,
		[classes[`select${type?.toUpperCase()}`]]: true,
		[classes.open]: isOpen,
		[classes.loading]: loading,
	});

	const selectWrapperClasses = classNames({
		[classes.alternativeSelectWrapper]: true,
	});

	const selectedItemWrapperClasses = classNames({
		[classes.selectedItemWrapperWithPadding]: withHorizontalPadding,
		[classes.selectedItemWrapper]: true,
		[classes[`selectedItemWrapper${type?.toUpperCase()}`]]: true,
		[classes.selectedItemWrapperOpen]: isOpen,
		[classes.selectedItemWrapperDisabled]: disabled,
		[classes.selectedItemWrapperError]: !!error || !!showError,
		[classes.alternativeSelectWrapperWithBorder]: showBorder,
	});

	const itemIconWrapperClasses = classNames({
		[classes.itemIconWrapper]: true,
		[classes[`itemIconWrapper${type?.toUpperCase()}`]]: true,
		[classes.itemIconWrapperDisabled]: disabled,
	});

	const arrowIconClasses = classNames({
		[classes.arrowIcon]: true,
		[classes[`arrowIcon${type?.toUpperCase()}`]]: true,
		[classes.rotatedArrowIcon]: isOpen,
	});

	const customArrowIconClasses = classNames({
		[classes.rotatedArrowIcon]: isOpen,
		[classes.customArrowIcon]: true,
	});
	const calculatedHeight = React.useMemo(() => {
		if (!isOpen) {
			return 0;
		}
		if (loading) {
			return 2 * height + 4;
		}
		if (items.length > 4) {
			return 6 * height - 5;
		}
		if (items.length === 0) {
			return height + 70;
		}
		if (items.length === 1) {
			return height + 90;
		}
		return items.length * height + 30;
	}, [isOpen, loading, items, height]);

	const onMenuItemClick = (item: string | IAlternativeSelectItem) => {
		if (!isMenuItemDisabled(item)) {
			onClick(item);
			setIsOpen(false);
		}
	};

	const renderArrow = dropdownIcon ? (
		<span
			className={customArrowIconClasses}
			onClick={() => {
				if (dropdownIconClick && setRenderChildren) {
					dropdownIconClick(!renderChildren);
				}
			}}
		>
			{dropdownIcon}
		</span>
	) : (
		<KeyboardArrowDown className={arrowIconClasses} />
	);
	return (
		<div
			id={id}
			data-testid={id}
			className={classNames({
				[classes.alternativeSelect]: true,
				[classes.alternativeSelectMD]: type === 'md',
				[`${className}`]: !!className,
			})}
		>
			{label && (
				<div className={classes.label}>
					<BodyS disableMargin>{label}</BodyS>
				</div>
			)}
			<div
				className={selectWrapperClasses}
				tabIndex={0}
				onBlur={() => {
					if (hideOnBlur) {
						setIsOpen(false);
						if (setRenderChildren) return setRenderChildren(!renderChildren);
					}
				}}
				onKeyDown={(e) => {
					//for accessibility
					if (e.keyCode === 13) {
						if (setRenderChildren) return setRenderChildren(!renderChildren);
						setIsOpen(true);
					}
				}}
			>
				<div
					onClick={() => {
						//to open custom component rather than normal drop down
						if (setRenderChildren) {
							setIsOpen((value) => !value);
							return setRenderChildren(!renderChildren);
						}

						return !disabled && setIsOpen((value) => !value);
					}}
					className={selectedItemWrapperClasses}
				>
					<div className={itemIconWrapperClasses}>{value}</div>
					{selectedItems.length > 0 && clear ? (
						<Close
							className={classes.closeIcon}
							onClick={(event) => {
								event.stopPropagation();
								clear();
							}}
						/>
					) : (
						renderArrow
					)}
				</div>
				{renderChildren
					? children
					: showItems && (
							<div
								className={selectClasses}
								style={{
									height: `${calculatedHeight}px`,
								}}
							>
								{loading ? (
									<div>
										<CustomCircularProgress
											text={translate('number.loading.packages')}
											loading={loading}
										/>
									</div>
								) : (
									<>
										{searchOption && (
											<div className={classes.searchInputWrapper}>
												<CustomInput
													id={id}
													formControlProps={{
														fullWidth: true,
													}}
													labelText={translate('search')}
													//@ts-ignore
													inputLabelClasses={classes.inputLabel}
													inputProps={{
														onFocus: () => setIsOpen(true), // set true to prevent close dropdown input on focus
														onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
															if (setSearchValue) {
																setSearchValue(event.target.value);
															}
														},
														value: searchValue,
													}}
												/>
												<SearchIcon className={classes.searchIcon} />
											</div>
										)}

										{items.map((item: string | IAlternativeSelectItem, index: number) => (
											<div
												key={(typeof item === 'string' ? item : item.key || item.id) || index}
												onMouseDown={() => {
													onMenuItemClick(item);
												}}
												onMouseEnter={() => !isMenuItemDisabled(item) && setActiveItem(index)}
												className={classNames({
													[classes.menuItem]: true,
													[classes[`menuItem${type?.toUpperCase()}`]]: true,
													[classes.activeMenuItem]: activeItem === index,
													[classNames(
														classes.selectedMenuItem,
														classes[`selectedMenuItem${type?.toUpperCase()}`]
													)]:
														selectedItems.indexOf(
															typeof item === 'string' ? item : item.id
														) >= 0,
													[classes.menuItemDisabled]: isMenuItemDisabled(item),
												})}
											>
												<div>
													<div className={itemIconWrapperClasses}>
														{customIcons ? (
															<CustomAvatar
																accountId={isSelectItem(item) ? item.id : item}
																alt="Company Logo"
																placeholderIcon={<Business />}
																styles={{
																	marginBottom: 0,
																}}
															/>
														) : typeof item === 'string' ? null : (
															!!item?.icon && (
																<div className={itemIconWrapperClasses}>
																	{item.icon}
																</div>
															)
														)}
													</div>

													<SelectItem type={type}>
														{typeof item === 'string' ? item : item.name}
													</SelectItem>
													<ConditionalRender
														show={showLoadingWhenItemDisabled && isMenuItemDisabled(item)}
														onTrue={() => {
															return (
																<CircularProgress
																	size={14}
																	className={classes.itemDisabledLoading}
																/>
															);
														}}
													/>
												</div>
												<Done />
											</div>
										))}
										{!renderChildren && items.length <= 0 && (
											<div className={classes.noResults}>
												<SelectItem disableMargin type={type}>
													{translate('no.results')}
												</SelectItem>
											</div>
										)}
									</>
								)}
							</div>
					  )}
			</div>
			<div className={classes.error}>
				<BodyS color="danger" disableMargin>
					{error}
				</BodyS>
			</div>
		</div>
	);
};

export default AlternativeSelect;
